import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useContext,
  useState,
  useEffect,
} from "react";
import clsx from "clsx";

import { Context } from "../../../../../studentStore";
import {
  IActiveLearningIssue,
  IActiveLearningAnswers,
} from "../../../../../state";
import { IAnswer } from "../../../../../constants/active_learning";
import TextInput from "../../../../../atoms/ActiveLearning/TextInput";
import TextArea from "../../../../../atoms/ActiveLearning/TextArea";
import styles from "../../ActiveLearningIssue.module.scss";
import { ReactComponent as IconLive } from "../../../../../assets/images/ActiveLearning/icon_live.svg";
import hintImage09 from "../../../../../assets/images/ActiveLearning/hint/img_09.webp";

interface IActiveLearningIssue1Props {
  issue?: IActiveLearningIssue;
  answers: IActiveLearningAnswers[];
  setAnswers: Dispatch<SetStateAction<IActiveLearningAnswers[]>>;
}

export interface ICurrentAnswer extends IAnswer {
  value: string;
}

export const Issue3 = (props: IActiveLearningIssue1Props) => {
  const { state } = useContext(Context);
  const currentIssueNo = 3;
  const question1 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 1);
  }, [props.issue?.questions]);
  const question2 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 2);
  }, [props.issue?.questions]);

  const [answer1, setAnswer1] = useState<ICurrentAnswer>({
    id: null,
    issue_no: currentIssueNo,
    question_no: question1?.order_num || NaN,
    question_id: question1?.id || NaN,
    value: "",
  });
  const [answer2, setAnswer2] = useState<ICurrentAnswer>({
    id: null,
    issue_no: currentIssueNo,
    question_no: question2?.order_num || NaN,
    question_id: question2?.id || NaN,
    value: "",
  });

  const setAnswerWraper = useCallback(
    (questionNo: number, setter: Dispatch<React.SetStateAction<any>>) => {
      const targetAnswer = state.active_learning_answers.find(
        answer =>
          answer.issue_no === currentIssueNo &&
          answer.question_no === questionNo,
      );
      const answer: ICurrentAnswer = {
        id: targetAnswer?.id || null,
        issue_no: currentIssueNo,
        question_no: questionNo,
        question_id: targetAnswer?.question_id || 0,
        value: targetAnswer?.contents?.value || "",
      };
      setter({ ...answer });
    },
    [state.active_learning_answers],
  );

  useEffect(() => {
    setAnswerWraper(1, setAnswer1);
    setAnswerWraper(2, setAnswer2);
  }, [setAnswerWraper]);

  useEffect(() => {
    const answers = props.answers.filter(
      answer =>
        !(
          answer.issue_no === currentIssueNo &&
          [question1?.order_num, question2?.order_num].includes(
            answer.question_no,
          )
        ),
    );
    props.setAnswers([
      ...answers,
      ...[
        {
          id: answer1.id,
          issue_no: currentIssueNo,
          question_no: 1,
          question_id: question1?.id || NaN,
          contents: {
            value: answer1.value,
          },
        },
        {
          id: answer2.id,
          issue_no: currentIssueNo,
          question_no: 2,
          question_id: question2?.id || NaN,
          contents: {
            value: answer2.value,
          },
        },
      ],
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer1, answer2, question1, question2]);

  const handleChangeAnswer = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
    answer: ICurrentAnswer,
    setter: React.Dispatch<React.SetStateAction<any>>,
  ) => {
    const { value } = e.target;
    setter({
      id: answer.id,
      question_id: question1?.id || NaN,
      question_no: question1?.order_num || NaN,
      issue_no: currentIssueNo,
      value,
    });
  };

  return (
    <div className={styles.issue}>
      <div className={styles.header}>
        <span className={styles.no}>
          WORK
          {props.issue?.no && props.issue?.no < 10
            ? `0${props.issue?.no}`
            : props.issue?.no}
        </span>
        <p className={styles.title}>{props.issue?.title}</p>
        <p className={styles.read}>
          理想状況をつくり出すために、どのような行動をすれば良いかを考え、今後取り組むべき「課題」を設定しよう。
        </p>
        <p className={styles.example}>
          例：「問い」/○○県〇〇市の若者が大学を卒業したときに地元を離れないようにするためには？→「課題」/〇〇市の子育て支援を充実させ、〇〇市の魅力が若者に伝わるようにする。
        </p>
      </div>
      {question1 && (
        <TextInput
          value={answer1.value}
          handleChange={e => handleChangeAnswer(e, answer1, setAnswer1)}
        />
      )}
      <div className={clsx(styles.card, styles.row)}>
        <div className={styles.wrapper}>
          <div className={styles.block}>
            <p className={styles.label}>
              <IconLive />
              POINT
            </p>
            <p className={styles.title}>「課題」とは？</p>
            <p className={styles.text}>
              探究学習において、「問い」に答えを出すためにやるべきことを「課題」と呼ぶ。
              <br />
              問題や原因を整理せずに「問い」に答えを出そうとすると、不足や迷いが生まれ、答えを出すのが難しくなる。これまでに考えてきた問題/原因/理想状況を総合的に見て「課題」を設定することで、「問い」の実現性を高めることができる。
            </p>
          </div>
          <div className={styles.block}>
            <img
              className={styles.hintImage}
              src={hintImage09}
              alt="問い／問題／原因／理想状況／課題"
            />
          </div>
        </div>
      </div>
      {question2 && (
        <div className={styles.themEntry}>
          <p className={styles.title}>{question2.title}</p>
          <p className={styles.read}>
            これまでのLessonで、「問い」に答えを出すために、やるべき「課題」を設定した。
            <br />
            原因を調べたときにどんな気づきや学びがあったのか、理想状況からどのように「課題」を設定したのかを思い出して、気づいたことや考えたことを入力しよう。
          </p>
          <p className={styles.example}>
            例：問題がなぜ起きているのかを調べたときに、〇〇の問題が大きいと思っていたのに実際に大きな問題だったのは△△の問題だった。3つの理想状況を合わせたら──
          </p>
          <div className={styles.textAreaWrapper}>
            <TextArea
              value={answer2.value}
              handleChange={e => handleChangeAnswer(e, answer2, setAnswer2)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
export default Issue3;
